// src/utils/storeUtils.js
/**
* Calculate distance between two coordinates using Haversine formula
 * @param {number} lat1 - Latitude of point 1
 * @param {number} lon1 - Longitude of point 1
 * @param {number} lat2 - Latitude of point 2
 * @param {number} lon2 - Longitude of point 2
 * @returns {number} Distance in kilometers
 */
export const calculateDistance = (lat1, lon1, lat2, lon2) => {
  // Use slightly larger Earth radius to account for road winding/elevation
  const R = 6378; // Earth's equatorial radius in km
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lon2 - lon1) * Math.PI / 180;
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
    Math.sin(dLon / 2) * Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

  // Calculate straight line distance
  const straightLineDistance = R * c;

  // Apply a "Road Factor" (Tortuosity)
  // Google Maps calculates driving distance, which is always longer than straight line.
  // Average urban road efficiency is ~1.3 to 1.5 times the straight line distance.
  const ROAD_FACTOR = 1.35;

  return straightLineDistance * ROAD_FACTOR;
};

/**
 * Find nearest store based on user location
 * @param {Array} stores - Array of store objects with latitude/longitude
 * @param {Object} userLocation - User's location {latitude, longitude}
 * @returns {Object|null} Nearest store with distance property
 */
export const findNearestStore = (stores, userLocation) => {
  if (!userLocation || !userLocation.latitude || !stores || stores.length === 0) {
    return null;
  }

  const storesWithDistance = stores
    .filter(store => store.latitude && store.longitude)
    .map(store => ({
      ...store,
      distance: calculateDistance(
        userLocation.latitude,
        userLocation.longitude,
        parseFloat(store.latitude),
        parseFloat(store.longitude)
      )
    }))
    .sort((a, b) => a.distance - b.distance);

  return storesWithDistance.length > 0 ? storesWithDistance[0] : null;
};

/**
 * Format store hours from week_schedule grouping contiguous days
 * @param {Object} weekSchedule - Week schedule object from API
 * @returns {Array} Formatted schedule array
 */
export const formatStoreHours = (weekSchedule) => {
  if (!weekSchedule) return [];

  const dayOrder = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
  const dayLabels = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Saturday', 'Sunday']; // Full names for weekends as in reference

  // 1. Generate formatted strings for each day
  const dailyTimes = dayOrder.map((day, index) => {
    const daySchedule = weekSchedule[day];
    let timeStr = 'Holiday';

    if (daySchedule && daySchedule.is_open) {
      if (Array.isArray(daySchedule.schedules) && daySchedule.schedules.length > 0) {
        timeStr = daySchedule.schedules
          .map(slot => `${formatTime(slot.open_time)} to ${formatTime(slot.close_time)}`)
          .join(', ');
      } else if (daySchedule.open_time && daySchedule.close_time) {
        timeStr = `${formatTime(daySchedule.open_time)} to ${formatTime(daySchedule.close_time)}`;
      }
    }

    return { label: dayLabels[index], time: timeStr };
  });

  // 2. Group consecutive days with the same time string
  const groupedSchedule = [];
  let currentGroup = null;

  dailyTimes.forEach((dayData) => {
    if (!currentGroup) {
      currentGroup = { startLabel: dayData.label, endLabel: dayData.label, time: dayData.time };
    } else if (currentGroup.time === dayData.time) {
      currentGroup.endLabel = dayData.label; // Extend group
    } else {
      // Time changed, push current group
      const dayLabel = currentGroup.startLabel === currentGroup.endLabel
        ? currentGroup.startLabel
        : `${currentGroup.startLabel} - ${currentGroup.endLabel}`;

      groupedSchedule.push({
        day: dayLabel,
        time: currentGroup.time
      });
      // Start new group
      currentGroup = { startLabel: dayData.label, endLabel: dayData.label, time: dayData.time };
    }
  });

  // Push the final group
  if (currentGroup) {
    const dayLabel = currentGroup.startLabel === currentGroup.endLabel
      ? currentGroup.startLabel
      : `${currentGroup.startLabel} - ${currentGroup.endLabel}`;

    groupedSchedule.push({
      day: dayLabel,
      time: currentGroup.time
    });
  }

  // Optional: if the ENTIRE week is "Holiday" (i.e. empty schedule), don't return 1 element
  if (groupedSchedule.length === 1 && groupedSchedule[0].time === 'Holiday') {
    return [];
  }

  return groupedSchedule;
};

/**
 * Format time from 24-hour to 12-hour format, matching the reference (e.g. 9AM, 5PM)
 * @param {string} time - Time in HH:MM format
 * @returns {string} Formatted time
 */
const formatTime = (time) => {
  if (!time) return '';

  const [hours, minutes] = time.split(':');
  const hour = parseInt(hours);
  const ampm = hour >= 12 ? 'PM' : 'AM';
  const displayHour = hour % 12 || 12;

  // Simplify '9:00 AM' to '9AM' to match the reference image exactly
  if (minutes === '00') {
    return `${displayHour} ${ampm}`;
  }

  return `${displayHour}:${minutes} ${ampm}`;
};

/**
 * Capitalize first letter
 * @param {string} str - String to capitalize
 * @returns {string} Capitalized string
 */
export const capitalize = (str) => {
  if (!str) return '';
  return str.charAt(0).toUpperCase() + str.slice(1);
};

/**
 * Format store address into a clean, concise location string
 * Example: "Ameerpet, Hyderabad, Telangana"
 * Strips door numbers, pincodes, and store name duplicates
 * @param {Object} store - Store / branch object with address, city, state, name fields
 * @returns {string} Short formatted address
 */
export const getShortAddress = (store) => {
  if (!store) return '';
  if (store.short_address) return store.short_address;

  const city = (store.city || '').trim();
  const state = (store.state || '').trim();
  const cityLower = city.toLowerCase();
  const stateLower = state.toLowerCase();
  const storeNameLower = (store.name || '').toLowerCase();

  const sourceText = store.address || store.location || '';
  if (!sourceText) return [city, state].filter(Boolean).join(', ');

  const parts = sourceText.split(',').map(p => p.trim()).filter(Boolean);
  const cleanParts = [];

  parts.forEach(part => {
    let cleaned = part.replace(/\b\d{6}\b/g, '').trim();
    if (!cleaned) return;

    // Remove door / plot numbers (e.g. "8-3-219/1", "#12-3", "H.No 5-6")
    cleaned = cleaned.replace(/^(#|H\.?No\.?|Plot\.?|Flat\.?|Door\.?|D\.?No\.?)?\s*\d+[\d\/\-\w]*\s*/i, '').trim();
    if (!cleaned) return;

    const cleanedLower = cleaned.toLowerCase();

    // Skip parts that match or contain the store name (already shown as card title)
    if (storeNameLower && (cleanedLower === storeNameLower || storeNameLower.includes(cleanedLower) || cleanedLower.includes(storeNameLower))) return;

    // Skip exact city / state matches (will be appended)
    if (cleanedLower === cityLower || cleanedLower === stateLower) return;

    // Deduplicate
    if (!cleanParts.some(cp => cp.toLowerCase() === cleanedLower)) cleanParts.push(cleaned);
  });

  // Pick the most relevant area (last locality part tends to be area/suburb)
  const areaName = cleanParts.length > 0 ? cleanParts[cleanParts.length - 1] : '';

  const result = [];
  if (areaName) result.push(areaName);
  if (city && !result.some(p => p.toLowerCase() === cityLower)) result.push(city);
  if (state && !result.some(p => p.toLowerCase() === stateLower)) result.push(state);

  return result.join(', ');
};